import { OCI_LOGGING_CONFIG } from '../config';
import { LogEntry } from '../types';

// Universal crypto helper
const getSubtleCrypto = (): SubtleCrypto | null => {
    // 1. Browser context (standard)
    if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
        return window.crypto.subtle;
    }
    // 2. Node.js context (globalThis.crypto is available in Node 19+)
    const globalCrypto = (globalThis as any).crypto;
    if (globalCrypto && globalCrypto.subtle) {
        return globalCrypto.subtle;
    }
    // No easy way to get it synchronously in older Node without require, 
    // but Node 18+ is standard now for these projects.
    return null;
};

export class LogExporter {
    private static instance: LogExporter;
    private logQueue: LogEntry[] = [];
    private maxBatchSize: number = 20;
    private isExporting: boolean = false;
    private privateKey: any | null = null;
    private keyId: string;

    private constructor() {
        this.keyId = `${OCI_LOGGING_CONFIG.TENANCY_ID}/${OCI_LOGGING_CONFIG.USER_ID}/${OCI_LOGGING_CONFIG.KEY_FINGERPRINT}`;
        this.initializeKey();
    }

    public static getInstance(): LogExporter {
        if (!LogExporter.instance) {
            LogExporter.instance = new LogExporter();
        }
        return LogExporter.instance;
    }

    private async initializeKey() {
        if (OCI_LOGGING_CONFIG.MOCK_LOGS) return;

        const pem = OCI_LOGGING_CONFIG.PRIVATE_KEY;
        if (!pem || pem.includes('...')) return;

        try {
            const subtle = getSubtleCrypto();
            if (!subtle) return;

            // Clean PEM for import
            const pemContents = pem
                .replace(/-----BEGIN RSA PRIVATE KEY-----/g, '')
                .replace(/-----END RSA PRIVATE KEY-----/g, '')
                .replace(/[\r\n]/g, '')
                .trim();
            
            // Standard base64 decoding (works in both Node and Browser)
            const binaryDerString = typeof window !== 'undefined' 
                ? window.atob(pemContents)
                : Buffer.from(pemContents, 'base64').toString('binary');
            
            const binaryDer = new Uint8Array(binaryDerString.length);
            for (let i = 0; i < binaryDerString.length; i++) {
                binaryDer[i] = binaryDerString.charCodeAt(i);
            }

            this.privateKey = await subtle.importKey(
                "pkcs8",
                binaryDer.buffer,
                { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
                false,
                ["sign"]
            );
        } catch (e) {
            // Silently fail to not break the app if logger fails
            // console.error('[LogExporter] Key initialization failed', e);
        }
    }

    public enqueue(entry: LogEntry) {
        if (OCI_LOGGING_CONFIG.MOCK_LOGS) return;
        this.logQueue.push(entry);

        if (this.logQueue.length >= this.maxBatchSize && !this.isExporting) {
            this.export();
        }
    }

    public async export(): Promise<void> {
        if (this.logQueue.length === 0 || this.isExporting || !this.privateKey) return;

        this.isExporting = true;
        const batch = [...this.logQueue];
        this.logQueue = [];

        try {
            const subtle = getSubtleCrypto();
            if (!subtle) throw new Error('Crypto not available');

            const payload = JSON.stringify({
                specversion: "1.0",
                logEntryBatches: [{
                    entries: batch.map(log => ({
                        logContent: { data: log.message, context: log.context },
                        time: log.timestamp,
                        source: log.level
                    })),
                    source: "ConstantinaApp",
                    type: "com.oraclecloud.logging.custom.json",
                    subject: "ApplicationLogs",
                    defaultlogGroupId: OCI_LOGGING_CONFIG.LOG_GROUP_ID
                }]
            });

            const date = new Date().toUTCString();
            const url = new URL(`${OCI_LOGGING_CONFIG.ENDPOINT}/20200601/logEvents`);
            
            const encoder = new TextEncoder();
            const payloadBuffer = encoder.encode(payload);
            const hashBuffer = await subtle.digest('SHA-256', payloadBuffer);
            
            // Base64 helper
            const ab2base64 = (buf: ArrayBuffer) => {
                const bytes = new Uint8Array(buf);
                const binary = Array.from(bytes).map(b => String.fromCharCode(b)).join('');
                return typeof window !== 'undefined' ? window.btoa(binary) : Buffer.from(bytes).toString('base64');
            };

            const contentSha256 = ab2base64(hashBuffer);
            const contentType = "application/json";
            const contentLength = payloadBuffer.byteLength.toString();

            const headersToSign = [
                `(request-target): post ${url.pathname}${url.search}`,
                `date: ${date}`,
                `host: ${url.host}`,
                `x-content-sha256: ${contentSha256}`,
                `content-type: ${contentType}`,
                `content-length: ${contentLength}`
            ];
            const signingString = headersToSign.join('\n');

            const signatureBuffer = await subtle.sign(
                "RSASSA-PKCS1-v1_5",
                this.privateKey,
                encoder.encode(signingString)
            );
            const signature = ab2base64(signatureBuffer);

            const authHeader = `Signature version="1",keyId="${this.keyId}",algorithm="rsa-sha256",headers="(request-target) date host x-content-sha256 content-type content-length",signature="${signature}"`;

            await fetch(url.toString(), {
                method: 'POST',
                headers: {
                    'Date': date,
                    'Content-Type': contentType,
                    'Content-Length': contentLength,
                    'x-content-sha256': contentSha256,
                    'Authorization': authHeader
                },
                body: payload
            });
        } catch (error) {
            // console.error('[LogExporter] Export failed:', error);
        } finally {
            this.isExporting = false;
        }
    }
}

export const logExporter = LogExporter.getInstance();
